import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Box, Container } from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';

import { SCREEN_SIZE } from '../../constants';

export type CollapsiblePackageSectionProps = {
  icon: ReactNode;
  headerContent: ReactNode;
  collapseButton?: ReactNode;
  sectionContent: ReactNode;
  isOpen?: boolean;
  showButton?: boolean;
  className?: string;
};

const getComponentStyles = (path: string) =>
  themeGet(`components.collapsiblePackageSection.${path}`);

const Wrapper = styled(Box)`
  background: ${({ isOpen }: { isOpen?: boolean }) =>
    isOpen
      ? getComponentStyles('background.open')
      : getComponentStyles('background.closed')};
`;

const StyledContainer = styled(Container)<{
  isOpen?: boolean;
  showButton?: boolean;
}>`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'icon headerContent'
    ${({ isOpen }: { isOpen?: boolean }) =>
      isOpen ? `'sectionContent sectionContent'` : null}
    ${({ showButton }: { showButton?: boolean }) =>
      showButton ? `'collapseButton collapseButton'` : null};

  row-gap: ${getComponentStyles('gap.mobile.row')};
  column-gap: ${getComponentStyles('gap.mobile.column')};

  padding-top: ${getComponentStyles('padding.mobile.vertical')};
  padding-bottom: ${getComponentStyles('padding.mobile.vertical')};

  ${SCREEN_SIZE.MD} {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      ${({ showButton }: { showButton?: boolean }) =>
        showButton
          ? `'icon headerContent collapseButton'`
          : `'icon headerContent headerContent'`}
      ${({ isOpen }: { isOpen?: boolean }) =>
        isOpen ? `'sectionContent sectionContent sectionContent'` : null};

    row-gap: ${getComponentStyles('gap.row')};
    column-gap: ${getComponentStyles('gap.column')};

    padding-top: ${getComponentStyles('padding.vertical')};
    padding-bottom: ${getComponentStyles('padding.vertical')};
  }
`;

const IconBox = styled(Box)`
  grid-area: icon;
`;

const HeaderContentBox = styled(Box)`
  grid-area: headerContent;
  display: grid;
  row-gap: ${getComponentStyles('heading.gap')};
`;

const CollapseButtonBox = styled(Box)`
  grid-area: collapseButton;
`;

const SectionContentBox = styled(Box)`
  grid-area: sectionContent;
`;

const CollapsiblePackageSection = ({
  icon,
  headerContent,
  sectionContent,
  collapseButton,
  isOpen = false,
  showButton = true,
  className,
}: CollapsiblePackageSectionProps) => {
  return (
    <Wrapper isOpen={isOpen}>
      <StyledContainer
        as="section"
        className={className ?? ''}
        showButton={showButton}
        isOpen={isOpen}
      >
        <IconBox data-testid="COLLAPSIBLE_ICON">{icon}</IconBox>
        <HeaderContentBox data-testid="COLLAPSIBLE_HEADER">
          {headerContent}
        </HeaderContentBox>
        {isOpen && (
          <SectionContentBox data-testid="COLLAPSIBLE_CONTENT">
            {sectionContent}
          </SectionContentBox>
        )}
        {showButton && (
          <CollapseButtonBox data-testid="COLLAPSIBLE_BUTTON">
            {collapseButton}
          </CollapseButtonBox>
        )}
      </StyledContainer>
    </Wrapper>
  );
};

export default CollapsiblePackageSection;
